import * as $ from 'jquery';
import { DateTime } from 'luxon';

import {
  DateUtil,
  isNoValueString,
  JQueryClipboardEvent,
  JQueryFocusEvent,
  JQueryKeyboardEvent,
  KeyboardInput,
  NavDirection
} from '../common';

import { TemporalComponent, TemporalInputElement } from '../temporal-component';

import './time-input.component.scss';
import TEMPLATE from './time-input.component.pug';

const DATA_HOURS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(hour => hour.toString());
const DATA_MINUTES = [...Array(60).keys()].map(min => min.toString().padStart(2, '0'));
const DATA_MERIDIAN = ['AM', 'PM'];

const VALID_TIME_INPUT = /\d/;
const VALID_HOUR = /^(?:1[0-2])$|^(?:0?[1-9])$/;
const VALID_MINUTE = /^[0-5]?[0-9]$/;
const VALID_MERIDIAN_INPUT = /[ap]/i;
const VALID_MERIDIAN = /^[ap]m?$/i;

const DEFAULT_DISABLED_PLACEHOLDER = '-';

DATA_HOURS.forEach(hour => {
  if (!VALID_HOUR.test(hour)) {
    throw new Error(`Invalid hour ${hour}`);
  }
});
DATA_MINUTES.forEach(minute => {
  if (!VALID_MINUTE.test(minute)) {
    throw new Error(`Invalid minute ${minute}`);
  }
});

type HourMinuteDisplayPlaceholder = [string, string, ''];
type HourMinuteMeridianPlaceholder = [string, string, string];
type MultiPartDisplayPlaceholder = HourMinuteDisplayPlaceholder | HourMinuteMeridianPlaceholder;

function isMultiPartDisplayPlaceholder(obj: string[]): obj is MultiPartDisplayPlaceholder {
  return obj?.length === 3;
}

export interface TimeInputElement extends TemporalInputElement {
  // placeholder for now
}

declare global {
  interface HTMLElementTagNameMap {
    'iw-time-input': TimeInputElement;
  }

  interface JQuery {
    iwTimeInput(): JQuery<TimeInputElement>;
  }
}

type JQueryTimeInputPart = JQuery<HTMLInputElement>;

function checkTimeZoneSupport(): void {
  if (!DateUtil.TIME_ZONE_SUPPORT) {
    console.warn(
      'This browser does not support using explicit time zones when working with date/time values. ' +
      'Some features of the <iw-time-input> component may not work as expected.'
    )
  }
}

export class TimeInputComponent extends TemporalComponent<TimeInputElement> {

  public static readonly COMPONENT_SELECTOR = 'iw-time-input';
  public static readonly TEMPLATE = TEMPLATE;

  public static loadPlugin(): void {
    super.loadPlugin();
    if (!$.fn.iwTimeInput) {
      $.fn.iwTimeInput = this.jQueryPlugin('TimeInputComponent', undefined, checkTimeZoneSupport);
    }
  }

  public get placeholder(): string {
    return this._placeholder;
  }
  public set placeholder(placeholder: string) {
    this._placeholder = placeholder;
    this.syncPlaceholder(this.disabled);
  }

  public get disabledPlaceholder(): string | boolean {
    const attrValue = super.disabledPlaceholder;
    if (attrValue === false) {
      return false;
    }
    return attrValue || DEFAULT_DISABLED_PLACEHOLDER;
  }

  public get hasInput(): boolean {
    return this.parts.some(part => !!$(part).val());
  }

  private _displayPlaceholder: [string, string, string];
  public get displayPlaceholder(): [string, string, string] {
    return this._displayPlaceholder;
  }

  public get hasDisplayPlaceholder(): boolean {
    return !!this.displayPlaceholder;
  }

  public $spacer: JQuery;
  public $parts: JQueryTimeInputPart;
  public parts: HTMLInputElement[];
  public $hour: JQueryTimeInputPart;
  public $minute: JQueryTimeInputPart;
  public $meridian: JQueryTimeInputPart;

  // note: this is required to ensure that inheritance works correctly
  constructor($el: JQuery<TimeInputElement>) {
    super($el);
  }

  /**
   * Returns a jQuery selector of all tabbable elements in the document.
   *
   * Used to determine next/previous tab navigation targets.
   */
  public getTabbable(): JQuery {
    return $('input,button,select,textarea,object,a[href],[tabindex]').filter(':visible:not([tabindex^=-])');
  }

  protected getDisplayInput(): JQuery {
    return this.$el.find('input.time-input__part');
  }

  protected getDisplayPlaceholder(): JQuery {
    return this.$display.filter('[data-part=hour]');
  }

  protected getValueInput(): JQuery<HTMLInputElement> {
    return this.$el.find('input[type=hidden]') as JQuery<HTMLInputElement>;
  }

  protected initDomRefs(): void {
    super.initDomRefs();

    this.$el.attr('tabindex', this.$el.attr('tabindex') || '0');
    this.$value.attr('name', this.$el.attr('name'));

    this.$spacer = this.$el.find('.time-input__spacer');

    this.$parts = this.$display as JQueryTimeInputPart;
    this.parts = this.$parts.toArray();

    this.$hour = this.$parts
      .filter('[data-part=hour]')
      .data('data', DATA_HOURS)
      .data('input-validator', VALID_TIME_INPUT)
      .data('validator', VALID_HOUR);
    this.$minute = this.$parts
      .filter('[data-part=minute]')
      .data('data', DATA_MINUTES)
      .data('input-validator', VALID_TIME_INPUT)
      .data('validator', VALID_MINUTE);
    this.$meridian = this.$parts
      .filter('[data-part=meridian]')
      .data('data', DATA_MERIDIAN)
      .data('input-validator', VALID_MERIDIAN_INPUT)
      .data('validator', VALID_MERIDIAN);

    this.$parts.each((index, part) => {
      const $part = $(part);
      $part.prop('id', `${this.id}__${$part.data('part')}`);
    });
  }

  protected initEvents(): void {
    super.initEvents();
    this.$parts
      .on('input', this.onInput.bind(this))
      .on('keydown', KeyboardInput.wrapEvent(this.onInputKeyDown.bind(this)))
      .on('keyup', KeyboardInput.wrapEvent(this.onInputKeyUp.bind(this)))
      .on('focus', this.onInputFocus.bind(this))
      .on('blur', this.onInputBlur.bind(this))
      .on('copy', this.onCopy.bind(this))
      .on('paste', this.onPaste.bind(this));

    this.$el.on('focus', this.onComponentFocus.bind(this));
  }

  protected initDom(): void {
    this.onDisabledChange(this.disabled);
  }

  protected onInput(): void {
    // simulate the input event on the component when any part input has an input event
    this.$el.trigger('input');
  }

  protected onValue(value: DateTime, isChanged: boolean, isValid: boolean): void {
    super.onValue(value, isChanged, isValid);
    this.setInputValues(value);
    this.syncSpacer();
  }

  protected onInputKeyDown(e: JQueryKeyboardEvent<HTMLInputElement>): boolean {
    const $el = $(e.target);
    switch (e.key) {
      // allow modifier keys (required for shortcuts / reverse tabbing), deleting characters
      case 'Alt':
      case 'Backspace':
      case 'Delete':
      case 'Meta':
      case 'Shift':
        return true;

      case 'Tab':
        return e.shiftKey ? this.eventReturn(e, this.tabPrev()) : true;

      // allow common shortcuts
      case 'a':
      case 'c':
      case 'v':
      case 'x':
      case 'z':
        return this.eventReturn(e, e.metaKey || e.ctrlKey || this.validateKeyDown($el, e.key) && this.handleKeyDown($el, e.key));

      case ':':
        if (this.isHour($el)) {
          this.$minute.focus();
        }
        return this.eventReturn(e, false);

      case 'ArrowLeft':
      case 'ArrowRight':
        return this.eventReturn(e, this.arrowNav($el, e.key === 'ArrowRight' ? 1 : -1));


      case 'ArrowUp':
      case 'ArrowDown':
        this.incrementValue($el, e.key === 'ArrowUp' ? 1 : -1);
        return this.eventReturn(e, false);

      default:
        return this.eventReturn(e, this.validateKeyDown($el, e.key) && this.handleKeyDown($el, e.key));
    }
  }

  protected eventReturn(e: JQuery.TriggeredEvent, allowBubble: boolean): boolean {
    if (!allowBubble) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
    return allowBubble;
  }

  protected onInputKeyUp(e: JQueryKeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key ==='ArrowRight') {
      // for some reason, returning false from onInputKeyDown doesn't stop the keyboard event from bubbling to here
      // in Safari
      return;
    }
    const part = e.target;
    const $part = $(part);
    const maxLength = this.getMaxLength($part);
    if (maxLength && part.selectionStart === maxLength) {
      const arrowNavHandled = !this.arrowNav($part, 1);
      if (!arrowNavHandled) {
        this.tabNext();
      }
    }
  }

  protected onInputFocus(e: JQueryFocusEvent<HTMLInputElement>): void {
    const part = e.target;
    this.syncSpacer();
    // set the selection in a timeout so the mouseup doesn't undo the selection
    setTimeout(() => part.setSelectionRange(0, part.value.length));
  }

  protected onInputBlur(): void {
    if (!this.hasFocus) {
      this.syncValue();
    } else {
      this.syncSpacer();
    }
  }

  protected onComponentFocus(): void {
    this.focusPart(this.$hour[0]);
  }

  public validatePart($part: JQueryTimeInputPart, value?: string): boolean {
    value = typeof value === undefined ? this.getPartValue($part) : value;
    const validator = $part.data('validator');
    return validator.test(value);
  }

  protected getPartValue($part: JQueryTimeInputPart): string {
    return $part.val() as string;
  }

  protected getMaxLength($part: JQueryTimeInputPart): number {
    const maxLength = $part.prop('maxlength');
    if (typeof maxLength === 'number') {
      return maxLength;
    }
    return $part.prop('size');
  }

  protected validatePartInput($part: JQueryTimeInputPart, newValue: string, inputValue: string): boolean {
    const maxLength = this.getMaxLength($part);
    if (maxLength) {
      if (newValue.length === maxLength) {
        return this.validatePart($part, newValue);
      }
    }
    const inputValidator = $part.data('input-validator');
    if (inputValidator) {
      return inputValidator.test(inputValue);
    }
    return true;
  }

  protected onCopy(e: JQueryClipboardEvent): false {
    e.originalEvent.clipboardData.setData('text/plain', this.value);
    return false;
  }

  protected onPaste(e: JQueryClipboardEvent): false {
    const pastedInput = e.originalEvent.clipboardData.getData('text/plain');
    this.importValue(pastedInput);
    return false;
  }

  public syncValue(value?: DateTime): DateTime {
    if (value?.isValid === false && this.hasFocus) {
      // hold off updating if the component still has focus (e.g. the user is still editing)
      // this prevents things like deleting the content of a part from clearing the values from the rest of the parts
      return value;
    }

    if (arguments.length === 0) {
      value = this.getValueFromInputParts();
    }
    return super.syncValue(value);
  }

  protected getValueFromInputParts(): DateTime {
    const inputParts = this.getInputParts();

    if (!inputParts) {
      return undefined;
    }

    const date = this.getBaseDate();
    const [hour, minute, meridian] = inputParts;

    // note: using m instead of mm to avoid having to 0-prefix the minute
    const time = DateTime.fromFormat(`${hour}:${minute} ${meridian}`, 'h:m a');

    if (date?.isValid && time?.isValid) {
      const { year, month, day } = date;
      const { hour, minute } = time;

      return DateTime.fromObject({ year, month, day, hour, minute}, { zone: date.zoneName });
    }

    return this.getInvalidValue(date, time);
  }

  protected getInputParts(): [string, string, string] {
    const hourInput = this.getPartValue(this.$hour);
    const minuteInput = this.getPartValue(this.$minute);
    const meridianInput = this.getPartValue(this.$meridian)?.toLocaleUpperCase();

    if (isNoValueString(hourInput) && isNoValueString(minuteInput) && isNoValueString(meridianInput)) {
      return undefined;
    }
    return [hourInput, minuteInput, meridianInput];
  }

  protected getInvalidValue(date: DateTime, time: DateTime): DateTime {
    if (!date.isValid) {
      return date;
    }
    return time;
  }

  protected syncSpacer(): void {
    // show the spacer if any part has a value, focus, or if there is a multi-part display placeholder
    this.$spacer.toggle(this.hasFocus || this.hasInput || this.hasDisplayPlaceholder);
  }

  protected setDisplay(value: string): JQuery {
    if (!this.$parts) {
      // initDomRefs has not been called yet, initDom will call onDisabledChange again to make sure this happens
      return this.$display;
    }
    this.setInputValues(value ? this.parseValue(value) : undefined);
    return this.$display;
  }

  protected setInputValues(time: DateTime): void {
    // update if there's a valid value, or no value - leave inputs for invalid values as they are
    if (time?.isValid === true || typeof time === 'undefined') {
      this.$hour.val(this.formatInputPart(time, 'h'));
      this.$minute.val(this.formatInputPart(time, 'mm'));
      this.$meridian.val(this.formatInputPart(time, 'a'));
    }
  }

  protected formatInputPart(value: DateTime, format: string): string {
    if (typeof value === 'undefined') {
      return '';
    }
    return value.toFormat(format);
  }

  protected isHour($el: JQueryTimeInputPart): boolean {
    return $el[0] === this.$hour[0];
  }

  protected isMinute($el: JQueryTimeInputPart): boolean {
    return $el[0] === this.$minute[0];
  }

  protected isMeridian($el: JQueryTimeInputPart): boolean {
    return $el[0] === this.$meridian[0];
  }

  protected arrowNav($part: JQueryTimeInputPart, dir: NavDirection): boolean {
    const part = $part[0];
    const partIndex = this.parts.indexOf(part);
    const partValue = this.getPartValue($part);
    if (partIndex < 0) {
      throw new Error('oops!');
    }
    const prevPart = this.parts[partIndex - 1];
    const nextPart = this.parts[partIndex + 1];
    if (dir < 0 && part.selectionStart === 0) {
      this.focusPart(prevPart);
      return false;
    }
    if (dir > 0 && part.selectionEnd === partValue.length) {
      this.focusPart(nextPart);
      return false;
    }

    return true;
  }

  protected focusPart(part: HTMLInputElement): void {
    if (!part) {
      return;
    }
    part.focus();
  }

  protected incrementValue($part: JQueryTimeInputPart, amount: number, select = false): void {
    const data = $part.data('data');
    const curIndex = data.indexOf($part.val());
    let newIndex = curIndex < 0 ? 0 : (curIndex + amount);
    if (newIndex < 0) {
      newIndex = data.length + newIndex;
      if (this.isHour($part)) {
        this.incrementValue(this.$meridian, -1);
      } else if (this.isMinute($part)) {
        this.incrementValue(this.$hour, -1);
      }
    } else if (newIndex >= data.length) {
      if (this.isHour($part)) {
        this.incrementValue(this.$meridian, 1);
      } else if (this.isMinute($part)) {
        this.incrementValue(this.$hour, 1);
      }
      newIndex = newIndex - data.length;
    }
    const newValue = data[newIndex];
    $part.val(newValue);
    this.syncValue();
    // IMPORTANT: setting selection needs to happen AFTER syncValue, as syncValue can mess with the selection
    //            in Safari
    if (select) {
      $part[ 0 ].setSelectionRange(0, newValue.length);
    }
  }

  protected validateKeyDown($part: JQueryTimeInputPart, key: string): boolean {
    const part = $part[0];
    const value = (this.getPartValue($part) || '').split('');
    value.splice(part.selectionStart, part.selectionEnd - part.selectionStart, key);
    const newValue = value.join('');
    return this.validatePartInput($part, newValue, key);
  }

  protected handleKeyDown($part: JQueryTimeInputPart, key: string): boolean {
    if (this.isMeridian($part)) {
      if (key.toUpperCase() === 'M') {
        $part[0].setSelectionRange(2, 2);
        return false;
      }

      const assumedValue = DATA_MERIDIAN.find(meridian => meridian.startsWith(key.toUpperCase()));
      if (assumedValue) {
        $part.val(assumedValue);
        $part[0].setSelectionRange(1, assumedValue.length);
        return false;
      }
    }

    return true;
  }

  protected tabPrev(): false {
    const $tabbable = this.getTabbable();
    const index = $tabbable.index(this.el);
    const prevIndex = index - 1 < 0 ? $tabbable.length - 1 : index - 1;
    $tabbable[prevIndex].focus();
    return false;
  }

  protected tabNext(): false {
    const $tabbable = this.getTabbable();
    const index = $tabbable.index(this.el);
    const nextIndex = index + 1 === $tabbable.length ? 0 : index + 1;
    $tabbable[nextIndex].focus();
    return false;
  }

  private getMultipartDisplayPlaceholder(input: string): MultiPartDisplayPlaceholder {
    const placeholderParts = input.split(/[:\s]/g); // e.g. "h:m AM" -> ["h", "m", "AM"]
    if (placeholderParts.length === 2) {
      placeholderParts.push('');
    }
    if (isMultiPartDisplayPlaceholder(placeholderParts)) {
      return placeholderParts;
    }
    return undefined;
  }

  protected syncPlaceholder(disabled: boolean): void {
    if (disabled || !this.placeholder) {
      return super.syncPlaceholder(disabled);
    }

    const displayPlaceholder = this.getMultipartDisplayPlaceholder(this.placeholder);
    if (!displayPlaceholder) {
      return super.syncPlaceholder(disabled);
    }
    this._displayPlaceholder = displayPlaceholder;

    if (!this.$parts) {
      // initDomRefs has not been called yet, initDom will call onDisabledChange again to make sure this happens
      return;
    }

    const [hourPlaceholder, minutePlaceholder, meridianPlaceholder] = displayPlaceholder;
    this.$hour.prop('placeholder', hourPlaceholder);
    this.$minute.prop('placeholder', minutePlaceholder);
    this.$meridian.prop('placeholder', meridianPlaceholder);
  }
}
